import { 
  BirhausSearchableListValidation, 
  BirhausSearchableListCognitiveWarning,
  BirhausSearchableListMetrics,
  BirhausSearchableListProps
} from './types'

/**
 * BIRHAUS Utility Functions for Searchable List
 * Implements Miller's Law (7±2) and cognitive load principles
 */

/**
 * Validates if the list configuration complies with Miller's Law
 */
export function validateMillersLawCompliance<T>(
  items: T[],
  maxSelectable?: number,
  groupCount?: number
): BirhausSearchableListValidation {
  const warnings: BirhausSearchableListCognitiveWarning[] = []
  const recommendations: string[] = []
  let cognitiveLoadScore = 10 // Start with perfect score

  // Check item count
  if (items.length > 100) {
    warnings.push({
      type: 'high_item_count',
      message: `Lista con ${items.length} elementos puede causar sobrecarga cognitiva`,
      itemCount: items.length,
      recommendedAction: 'Habilitar virtualización y búsqueda',
      recommendation: 'Considere habilitar virtualización y búsqueda'
    })
    recommendations.push('Habilitar virtualización automática')
    recommendations.push('Implementar búsqueda con debounce')
    cognitiveLoadScore -= 2
  }

  // Check max selectable (Miller's Law: 7±2)
  if (maxSelectable && maxSelectable > 9) {
    warnings.push({
      type: 'high_selection_limit',
      message: `Límite de ${maxSelectable} selecciones excede la Ley de Miller`,
      itemCount: maxSelectable,
      recommendedAction: 'Reducir maxSelectable a 7',
      recommendation: 'Reducir a máximo 7-9 selecciones o implementar agrupación'
    })
    recommendations.push('Reducir maxSelectable a 7')
    recommendations.push('Implementar selección por grupos')
    cognitiveLoadScore -= 3
  }

  // Check group count
  if (groupCount && groupCount > 7) {
    warnings.push({
      type: 'high_item_count',
      message: `${groupCount} grupos exceden el límite cognitivo óptimo`,
      itemCount: groupCount,
      recommendedAction: 'Colapsar grupos inicialmente',
      recommendation: 'Considere sub-agrupación o colapsar grupos por defecto'
    })
    recommendations.push('Colapsar grupos inicialmente')
    recommendations.push('Implementar sub-categorías')
    cognitiveLoadScore -= 2
  }

  // Check if virtualization is needed
  if (items.length > 20) {
    recommendations.push('Habilitar virtualización para mejor rendimiento')
    if (items.length > 50) {
      cognitiveLoadScore -= 1
    }
  }

  // Check if search should be auto-enabled
  if (items.length > 20) {
    recommendations.push('Habilitar búsqueda automáticamente')
  }

  const isMillersLawCompliant = warnings.length === 0 && cognitiveLoadScore >= 7

  return {
    isValid: isMillersLawCompliant,
    errors: [],
    warnings,
    recommendations,
    isMillersLawCompliant,
    cognitiveLoadScore: Math.max(1, cognitiveLoadScore)
  }
}

/**
 * Calculate cognitive load metrics for the list
 */
export function calculateCognitiveMetrics<T>(
  items: T[],
  filteredItems: T[],
  selectedItems: T[],
  groups: Map<string, T[]> | null,
  isVirtualized: boolean,
  searchActive: boolean
): BirhausSearchableListMetrics {
  const groupCount = groups?.size || 0
  
  // Calculate cognitive score based on multiple factors
  let cognitiveScore = 10
  
  // Penalize for large item counts
  if (items.length > 100) cognitiveScore -= 2
  else if (items.length > 50) cognitiveScore -= 1
  
  // Penalize for too many selections
  if (selectedItems.length > 7) cognitiveScore -= 2
  else if (selectedItems.length > 5) cognitiveScore -= 1
  
  // Penalize for too many groups
  if (groupCount > 7) cognitiveScore -= 2
  else if (groupCount > 5) cognitiveScore -= 1
  
  // Reward for good practices
  if (isVirtualized && items.length > 20) cognitiveScore += 1
  if (searchActive && items.length > 20) cognitiveScore += 1
  if (groupCount > 0 && groupCount <= 7) cognitiveScore += 1
  
  return {
    totalItems: items.length,
    displayedItems: filteredItems.length,
    filteredItems: filteredItems.length,
    selectedItems: selectedItems.length,
    groups: groupCount,
    isVirtualized,
    searchActive,
    cognitiveScore: Math.max(1, Math.min(10, cognitiveScore))
  }
}

/**
 * Auto-configure list based on data size and BIRHAUS principles
 */
export function autoConfigureForCognitiveLoad<T>(
  items: T[],
  currentProps: Partial<BirhausSearchableListProps<T>>
): Partial<BirhausSearchableListProps<T>> {
  const optimizedProps: Partial<BirhausSearchableListProps<T>> = { ...currentProps }
  
  // Auto-enable virtualization for large lists
  if (items.length > 20 && currentProps.virtualize !== false) {
    optimizedProps.virtualize = true
  }
  
  // Auto-enable search for medium-large lists
  if (items.length > 10 && !currentProps.searchKeys) {
    // Enable search for all fields if no specific keys provided
    optimizedProps.searchKeys = []
  }
  
  // Set cognitive warnings for large lists
  if (items.length > 50 || (currentProps.maxSelectable && currentProps.maxSelectable > 7)) {
    optimizedProps.showCognitiveWarning = true
  }
  
  // Limit max selectable based on Miller's Law
  if (!currentProps.maxSelectable) {
    if (items.length > 100) optimizedProps.maxSelectable = 5
    else if (items.length > 50) optimizedProps.maxSelectable = 7
    else optimizedProps.maxSelectable = 9
  }
  
  // Auto-configure debounce based on list size
  if (!currentProps.debounceMs) {
    if (items.length > 1000) optimizedProps.debounceMs = 500
    else if (items.length > 100) optimizedProps.debounceMs = 300
    else optimizedProps.debounceMs = 150
  }
  
  return optimizedProps
}

/**
 * Filter items based on search term and keys
 */
export function filterItems<T>(
  items: T[],
  searchTerm: string,
  searchKeys: (keyof T)[] = []
): T[] {
  if (!searchTerm.trim()) return items
  
  const searchLower = searchTerm.toLowerCase().trim()
  
  return items.filter(item => {
    if (searchKeys.length === 0) {
      // Search all string/number properties
      return Object.values(item as any).some(value => {
        if (value == null) return false
        return String(value).toLowerCase().includes(searchLower)
      })
    }
    
    // Search specific keys only
    return searchKeys.some(key => {
      const value = item[key]
      if (value == null) return false
      return String(value).toLowerCase().includes(searchLower)
    })
  })
}

/**
 * Group items by a grouping function
 */
export function groupItems<T>(
  items: T[],
  groupBy: (item: T) => string
): Map<string, T[]> {
  const groups = new Map<string, T[]>()
  
  items.forEach(item => {
    const group = groupBy(item)
    if (!groups.has(group)) {
      groups.set(group, [])
    }
    groups.get(group)!.push(item)
  })
  
  // Sort groups by name for consistency
  return new Map([...groups.entries()].sort(([a], [b]) => a.localeCompare(b)))
}

/**
 * Get display name for an item (fallback chain)
 */
export function getItemDisplayName<T>(item: T, key: string): string {
  const itemAny = item as any
  
  // Try common display properties in order of preference
  const candidates = [
    itemAny.name,
    itemAny.title, 
    itemAny.label,
    itemAny.displayName,
    itemAny.text,
    key
  ]
  
  return candidates.find(candidate => 
    candidate != null && String(candidate).trim() !== ''
  ) || key
}

/**
 * Debounce function for search input
 */
export function createDebounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

/**
 * BIRHAUS presets for common use cases
 */
export const BirhausSearchableListPresets = {
  students: {
    searchKeys: ['name', 'email', 'studentId'] as any,
    maxSelectable: 7,
    showCognitiveWarning: true,
    cognitiveMode: 'warning' as const,
    searchPlaceholderEs: 'Buscar estudiantes...',
    searchPlaceholderEn: 'Search students...',
    emptyMessageEs: 'No hay estudiantes',
    emptyMessageEn: 'No students found',
    itemHeight: 72
  },
  
  churches: {
    searchKeys: ['name', 'region', 'pastor'] as any,
    groupBy: (item: any) => item.region || 'Sin región',
    maxSelectable: 5,
    showCognitiveWarning: true,
    cognitiveMode: 'strict' as const,
    searchPlaceholderEs: 'Buscar iglesias...',
    searchPlaceholderEn: 'Search churches...',
    emptyMessageEs: 'No hay iglesias',
    emptyMessageEn: 'No churches found',
    itemHeight: 80
  },
  
  employees: {
    searchKeys: ['name', 'email', 'department', 'position'] as any,
    groupBy: (item: any) => item.department || 'Sin departamento',
    maxSelectable: 6,
    showCognitiveWarning: true,
    cognitiveMode: 'warning' as const,
    searchPlaceholderEs: 'Buscar empleados...',
    searchPlaceholderEn: 'Search employees...',
    emptyMessageEs: 'No hay empleados',
    emptyMessageEn: 'No employees found',
    itemHeight: 76
  },
  
  transactions: {
    searchKeys: ['description', 'reference', 'amount'] as any,
    maxSelectable: 10,
    virtualize: true,
    showCognitiveWarning: false,
    cognitiveMode: 'disabled' as const,
    searchPlaceholderEs: 'Buscar transacciones...',
    searchPlaceholderEn: 'Search transactions...',
    emptyMessageEs: 'No hay transacciones',
    emptyMessageEn: 'No transactions found',
    itemHeight: 68
  },
  
  inventory: {
    searchKeys: ['name', 'sku', 'category', 'supplier'] as any,
    groupBy: (item: any) => item.category || 'Sin categoría',
    maxSelectable: 8,
    showCognitiveWarning: true,
    cognitiveMode: 'warning' as const,
    searchPlaceholderEs: 'Buscar productos...',
    searchPlaceholderEn: 'Search products...',
    emptyMessageEs: 'No hay productos',
    emptyMessageEn: 'No products found',
    itemHeight: 84
  }
} as const